import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { ROOT_URL } from "../../api/ApiConfig";

function authHeaders(getState) {
  const token = getState().loginToken?.response?.token;
  return {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  };
}

export function normalizeGroupTicketFromEmailEntry(entry) {
  if (!entry || typeof entry !== "object") return null;
  const email = entry.ticket_from_email ?? entry.email;
  return {
    group_id: entry.group_id ?? null,
    group_title: entry.group_title ?? "",
    ticket_from_email:
      email != null && String(email).trim() ? String(email).trim() : "",
    linked: Boolean(entry.linked),
    can_link: Boolean(entry.can_link),
    mail_auth_domain: entry.mail_auth_domain ?? null,
    mail_domain_verified: Boolean(entry.mail_domain_verified),
  };
}

export function getGroupTicketFromEmailEntry(ticketFromEmails, groupId) {
  if (!groupId || !Array.isArray(ticketFromEmails)) return null;
  const row = ticketFromEmails.find(
    (entry) => String(entry.group_id) === String(groupId),
  );
  return normalizeGroupTicketFromEmailEntry(row);
}

export function normalizePersonalTicketFromEmailData(payload) {
  const data = payload?.data ?? payload ?? {};
  const ticketFromEmail = data.ticket_from_email;
  const ticketFromEmails = Array.isArray(data.ticket_from_emails)
    ? data.ticket_from_emails
        .map(normalizeGroupTicketFromEmailEntry)
        .filter(Boolean)
    : [];

  return {
    ticket_from_email:
      ticketFromEmail != null && String(ticketFromEmail).trim()
        ? String(ticketFromEmail).trim()
        : "",
    linked: Boolean(data.linked),
    verified: Boolean(data.verified),
    requires_group_id: Boolean(data.requires_group_id),
    mail_auth_domain: data.mail_auth_domain ?? null,
    mail_domain_verified: Boolean(data.mail_domain_verified),
    verified_group_domains: Array.isArray(data.verified_group_domains)
      ? data.verified_group_domains
      : [],
    workspace_ticket_from_email: data.workspace_ticket_from_email ?? null,
    group_id: data.group_id ?? null,
    ticket_from_emails: ticketFromEmails,
  };
}

export function isDomainVerifiedForGroup(status, groupId) {
  if (!groupId || !status) return false;

  const verifiedDomains = status.verified_group_domains || [];
  if (verifiedDomains.length > 0) {
    return verifiedDomains.some((entry) => {
      if (entry == null) return false;
      if (typeof entry === "object") {
        const entryGroupId = entry.group_id ?? entry.groupId ?? entry.id;
        return entryGroupId != null && String(entryGroupId) === String(groupId);
      }
      return false;
    });
  }

  return status.mail_domain_verified === true;
}

export const fetchPersonalTicketFromEmailStatus = createAsyncThunk(
  "ticketFromEmail/fetchStatus",
  async ({ group_id } = {}, { getState, rejectWithValue }) => {
    if (!group_id) {
      return rejectWithValue({ message: "Missing group id" });
    }
    try {
      const response = await axios.get(
        `${ROOT_URL}/master-user/ticket-from-email`,
        {
          ...authHeaders(getState),
          params: { group_id },
        },
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message },
      );
    }
  },
);

export const updateTicketFromEmail = createAsyncThunk(
  "ticketFromEmail/update",
  async ({ group_id, ticket_from_email }, { getState, rejectWithValue }) => {
    const token = getState().loginToken?.response?.token;
    if (!getState().loginToken?.response?.user?._id) {
      return rejectWithValue({ message: "Not authenticated" });
    }

    const body = {
      ticket_from_email: String(ticket_from_email ?? "").trim(),
    };

    if (group_id) {
      body.group_id = group_id;
    }

    try {
      const response = await axios.patch(
        `${ROOT_URL}/master-user/ticket-from-email`,
        body,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        },
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message },
      );
    }
  },
);

const initialState = {
  ticket_from_email: "",
  linked: false,
  verified: false,
  mail_auth_domain: null,
  mail_domain_verified: false,
  verified_group_domains: [],
  workspace_ticket_from_email: null,
  group_id: null,
  requires_group_id: false,
  ticket_from_emails: [],
  fetchLoading: false,
  loading: false,
  error: null,
  loaded: false,
};

function applyPersonalTicketFromEmailState(state, payload) {
  const normalized = normalizePersonalTicketFromEmailData(payload);
  state.ticket_from_email = normalized.ticket_from_email;
  state.linked = normalized.linked;
  state.verified = normalized.verified;
  state.mail_auth_domain = normalized.mail_auth_domain;
  state.mail_domain_verified = normalized.mail_domain_verified;
  state.verified_group_domains = normalized.verified_group_domains;
  state.workspace_ticket_from_email = normalized.workspace_ticket_from_email;
  state.group_id = normalized.group_id;
  state.requires_group_id = normalized.requires_group_id;
  state.ticket_from_emails = normalized.ticket_from_emails;
}

const ticketFromEmailSlice = createSlice({
  name: "ticketFromEmail",
  initialState,
  reducers: {
    setTicketFromEmailLocal: (state, action) => {
      state.ticket_from_email = action.payload ?? "";
      state.linked = Boolean(String(action.payload ?? "").trim());
    },
    clearTicketFromEmailError: (state) => {
      state.error = null;
    },
    clearPersonalTicketFromEmail: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPersonalTicketFromEmailStatus.pending, (state) => {
        state.fetchLoading = true;
        state.error = null;
      })
      .addCase(fetchPersonalTicketFromEmailStatus.fulfilled, (state, action) => {
        state.fetchLoading = false;
        state.loaded = true;
        applyPersonalTicketFromEmailState(state, action.payload);
        const requestedGroupId = action.meta?.arg?.group_id;
        if (requestedGroupId) {
          state.group_id = requestedGroupId;
        }
      })
      .addCase(fetchPersonalTicketFromEmailStatus.rejected, (state, action) => {
        state.fetchLoading = false;
        state.loaded = true;
        state.error = action.payload;
      })
      .addCase(updateTicketFromEmail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTicketFromEmail.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload?.data) {
          applyPersonalTicketFromEmailState(state, action.payload);
          const groupIdArg = action.meta?.arg?.group_id;
          if (groupIdArg) {
            state.group_id = groupIdArg;
          }
          return;
        }
        const fromArg = action.meta?.arg?.ticket_from_email;
        const groupIdArg = action.meta?.arg?.group_id;
        state.ticket_from_email = String(fromArg ?? "").trim();
        state.linked = Boolean(state.ticket_from_email);
        if (groupIdArg) {
          state.group_id = groupIdArg;
        }
      })
      .addCase(updateTicketFromEmail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  setTicketFromEmailLocal,
  clearTicketFromEmailError,
  clearPersonalTicketFromEmail,
} = ticketFromEmailSlice.actions;
export default ticketFromEmailSlice.reducer;
